import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { translateSaveError } from '@/lib/mutation-result';
import {
  createLeaveCategory,
  deleteLeaveCategory,
  getLeaveCategories,
  updateLeaveCategory,
} from '../services/leave-category.service';
import {
  GetLeaveCategoriesParams,
  LeaveCategory,
  LeaveCategoryInsertInput,
  LeaveCategoryUpdateInput,
} from '../types/leave-category.types';

const invalidateLeaveCategoryQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-leave-categories'] });

export const useGetLeaveCategories = (params: GetLeaveCategoriesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-leave-categories', params],
    queryFn: () => getLeaveCategories(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateLeaveCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: LeaveCategoryInsertInput) => translateSaveError(t, createLeaveCategory(input)),
    onSuccess: () => {
      invalidateLeaveCategoryQueries(queryClient);
      toast({ title: t('LEAVE_TYPE_CREATED') });
    },
  });
};

export const useUpdateLeaveCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: LeaveCategoryUpdateInput }) =>
      translateSaveError(t, updateLeaveCategory(params)),
    onSuccess: () => {
      invalidateLeaveCategoryQueries(queryClient);
      toast({ title: t('LEAVE_TYPE_UPDATED') });
    },
  });
};

export const useDeleteLeaveCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (category: Pick<LeaveCategory, 'ItemId' | 'Title'>) =>
      translateSaveError(t, deleteLeaveCategory(category)),
    onSuccess: () => {
      invalidateLeaveCategoryQueries(queryClient);
      toast({ title: t('LEAVE_TYPE_DELETED') });
    },
  });
};
